// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo } from 'react';
import { ActionListRendererParams, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useStreamObserver } from '@Hooks/useStream';
import { TaskEventList } from '@Organisms/Task/eventList';
import { TaskOutput, TaskStatus, OrgPermissionsEnum, UserOutput } from 'thehive-sdk';
import { extractData } from '@Organisms/Task/extractData';
import Details from '@Molecules/Details/Details';
import useCasePermissions from '@Hooks/useCasePermissions';
import { useCurrentUser } from '@Queries/userQueries';
import { useGenericCountQuery, useGenericQuery } from '@Queries/genericQueries';
import { computeEventList as computeTaskLogEventList } from '@Organisms/TaskLog/eventList';
import client, { queryClient } from '@Client';
import useListQuery from '@Hooks/useListQuery';
import usePagination from '@Hooks/usePagination';
import { THForm } from '@Molecules/THForm/THForm';
import { Form } from 'antd';
import moment from 'moment';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ICONS } from '@Constants/iconsApp';
import { useListAllFields } from '@Queries/describeQueries';
import { computeEventList as computeCaseEventList } from '@Organisms/Case/eventList';
import { useDrawer } from '@Hooks/useDrawer';
import { computeGetSelector } from '@Organisms/Case/selector';

import './TaskDrawerData.scss';

interface TaskDrawerDataProps {
    task: TaskOutput;
    caseId: string;
    eventList: TaskEventList;
    actionList?: ActionListRendererParams<TaskOutput>;
    hasPermissions?: boolean;
}

const TaskDrawerData = ({
    task,
    caseId,
    eventList,
    actionList,
    hasPermissions = false,
}: TaskDrawerDataProps): ReactElement => {
    const i = useLocalMessage();

    const [form] = Form.useForm();

    const { data: describeData } = useListAllFields();
    const hasSharePermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_SHARE] });
    const { data: currentUser } = useCurrentUser();
    const { organisations = [], organisation = '' } = currentUser || {};
    const links = useMemo(
        () => organisations.find(({ organisation: orgName }) => orgName === organisation)?.links || [],
        [organisation, organisations],
    );
    const paginationManager = usePagination(10);
    const { itemsPerPage, page } = paginationManager;

    const [currentData, setCurrentData] = React.useState<TaskOutput>(task);
    const [updatedData, setUpdatedData] = React.useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = React.useState<boolean>(false);

    const [editMode, setEditMode] = React.useState(false);

    const { visible: visibleDrawer, setVisible: setVisibleDrawer } = useDrawer({});
    const [tmpRefetch, setTmpRefetch] = React.useState<string[]>([]);

    const { _id: taskId, _createdAt, _createdBy, _updatedAt, mandatory } = currentData;

    const caseEventList = useMemo(
        () =>
            computeCaseEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: () => {
                    return;
                },
            }),
        [i],
    );

    const { data: shareList = [], refetch: refetchShareList } = useGenericQuery({
        fetchCallback: () =>
            eventList.shares.onList(taskId, [
                {
                    field: 'organisationName',
                    type: 'string',
                    values: organisation,
                    operator: 'not',
                    describeAttribute: '' as any,
                },
            ]),
        key: ['sharings', taskId],
    });

    const { data: sharedOrgList = [], refetch: refetchSharedOrgList } = useGenericQuery({
        fetchCallback: () => caseEventList.shares.onList(caseId),
        key: ['sharedOrgList', caseId],
    });

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'assignableUsers' }],
                name: `get-task-${taskId}-assignable-users`,
                initialFilters: [
                    {
                        field: 'locked',
                        operator: 'any',
                        values: [false],
                        type: 'boolean',
                        describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
                    },
                ],
                sort: ['+name'],
            },
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: taskLogList = [], refetch: taskLogListRefetch } = useListQuery({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'logs' }],
                extraData: ['actionCount'],
                name: `action-count-task-logs-${taskId}`,
            },
            paginationOptions: { page, itemsPerPage },
            sort: ['-date'],
        },
        controller: client.TaskLogController,
    });

    const { data: taskLogCount = 0, refetch: taskLogCountRefetch } = useGenericCountQuery({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'logs' }],
                name: `get-task-logs-${taskId}`,
            },
        },
        controller: client.TaskLogController,
    });

    const taskLogEventList = useMemo(
        () =>
            computeTaskLogEventList({
                controller: client.TaskLogController,
                locale: i,
                onSuccess: (count, message) => {
                    setForceUpdate(true);
                    if (message) {
                        openMessage('success', message);
                    }
                    if (count) {
                        taskLogCountRefetch();
                    }
                    taskLogListRefetch();
                },
                onError: handleNotificationError,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i, taskId, taskLogListRefetch, taskLogCountRefetch],
    );

    const listTaskLogsIds: string[] = useMemo(() => taskLogList.map(({ _id }) => _id), [taskLogList]);

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.TASK },
        (data) => {
            const canRefetch = data.some((action) => action.base.objectId === taskId);

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    refetchShareList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchShareList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.CASE },
        (data) => {
            const canRefetchShare = data.some(({ base }) => {
                const { details } = base || {};
                return details?.share || details?.taskRule || details?.observableRule;
            });
            if (canRefetchShare) {
                if (!visibleDrawer && !editMode) {
                    refetchSharedOrgList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchSharedOrgList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.LOG },
        (data: any[]) => {
            const canRefetch = data.some((action) => {
                return (
                    (['create', 'delete'].includes(action.base?.action) && action.base?.object?.task?._id === taskId) ||
                    listTaskLogsIds.includes(action?.base?.objectId || '')
                );
            });

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    taskLogListRefetch();
                    taskLogCountRefetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'taskLogListRefetch', 'taskLogCountRefetch']);
                }
            }
        },
        [visibleDrawer, editMode, listTaskLogsIds],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.ACTION },
        (audits): void => {
            if (audits.some(({ base }) => base.details.objectType === 'Log')) {
                if (!visibleDrawer && !editMode) {
                    taskLogListRefetch();
                    taskLogCountRefetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'taskLogListRefetch', 'taskLogCountRefetch']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    const { status } = task || {};

    const forbiddenFields = useMemo(() => {
        const fields = [];

        if (!links.length) {
            fields.push('#computeShares');
        }
        if (status === TaskStatus.COMPLETED || status === TaskStatus.CANCEL) {
            fields.push('dueDate');
        } else {
            fields.push('endDate');
        }

        if (mandatory) {
            fields.push('status');
        } else {
            fields.push('#statusMandatory');
        }

        return fields;
    }, [status, links, mandatory]);

    useEffect(() => {
        form.setFieldsValue(currentData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData]);

    useEffect(() => {
        if (!visibleDrawer && !editMode && tmpRefetch.length) {
            tmpRefetch.includes('refetchShareList') && refetchShareList();
            tmpRefetch.includes('taskLogListRefetch') && taskLogListRefetch();
            tmpRefetch.includes('taskLogCountRefetch') && taskLogCountRefetch();
            setTmpRefetch([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleDrawer, editMode, tmpRefetch]);

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(task);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, updatedData]);

    useEffect(() => {
        if ((!editMode && task) || forceUpdate) {
            setCurrentData(task);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [task, editMode, forceUpdate, form]); // eslint-disable-line

    return (
        <THForm<TaskOutput & { dueDate: moment.Moment }>
            form={form}
            editable
            initialValues={currentData}
            onValuesChange={() => {
                setEditMode(true);
            }}
            //eslint-disable-next-line
            onFinish={({ _createdAt, _updatedAt, ...data }) => {
                eventList.onEdit(data, taskId).then(() => {
                    setForceUpdate(true);
                    if ('status' in data) {
                        queryClient.refetchQueries([computeGetSelector({ caseId }).name]);
                    }
                });
                setEditMode(false);
            }}
            submitTouchedValues
        >
            <THForm.Layout
                header={
                    <DisplayMetadata
                        items={[
                            {
                                icon: <ICONS.SettingIcon />,
                                label: 'id',
                                value: <CopyValue value={taskId}>{taskId}</CopyValue>,
                            },
                            {
                                icon: <ICONS.UserIcon />,
                                label: i('common.created-by'),
                                value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                            },
                            {
                                icon: <ICONS.CreationDateIcon />,
                                label: i('common.created-at'),
                                value: <FormatDate date={_createdAt} />,
                            },
                            {
                                icon: <ICONS.UpdateDateIcon />,
                                label: i('common.updated-at'),
                                value: <FormatDate date={_updatedAt} />,
                                hidden: !_updatedAt,
                            },
                        ]}
                    />
                }
                footer={
                    editMode ? (
                        <FormSubmitButton
                            onCancel={() => {
                                form.resetFields();
                                setForceUpdate(true);
                                setEditMode(false);
                            }}
                            disabled={false}
                            footerText={i('general.confirm')}
                        />
                    ) : (
                        <THDrawer.Footer
                            actions={
                                actionList && (
                                    <ActionsList
                                        actionList={actionList(
                                            [task],
                                            () => {
                                                //empty
                                            },
                                            'details',
                                        )}
                                        scope="single"
                                    />
                                )
                            }
                        >
                            <Link to={RouteBuilder.compute('cases.task', { caseId, taskId })}>
                                <THButton type="primary" block>
                                    {i('common.goToDetails')}
                                </THButton>
                            </Link>
                        </THDrawer.Footer>
                    )
                }
            >
                <Details
                    data={{
                        ...task,
                        '#statusMandatory': { valid: taskLogCount > 0 },
                        '#computeShares': { shareList, setVisible: setVisibleDrawer },
                        '#computeLogs': {
                            list: taskLogList,
                            count: taskLogCount,
                            paginationManager,
                            setVisible: setVisibleDrawer,
                        },
                    }}
                    extract={extractData({
                        data: task,
                        eventList,
                        taskLogEventList,
                        locale: i,
                        hasPermissions,
                        hasSharePermissions,
                        shareList,
                        sharedOrgList: sharedOrgList.filter(
                            ({ organisationName }) => currentUser?.organisation !== organisationName,
                        ),
                        assignableUsers,
                        refetchAssignableUsers,
                        organisation,
                        caseId,
                        formEditMode: editMode,
                    })}
                    forbidden={forbiddenFields}
                />
            </THForm.Layout>
        </THForm>
    );
};

export default TaskDrawerData;
